import type z from 'zod'

import type {
  ExternalAuthorization,
  OperationLevelPermission,
  PlatformWidePermission
} from './auth-claims/permissions'

type ErrorLike = {
  [K in keyof Error]: Error[K]
}

type CombinedSchemaOutput<A extends z.ZodType, B extends z.ZodType> = A extends z.ZodVoid
  ? z.infer<B>
  : B extends z.ZodVoid
    ? z.infer<A>
    : z.infer<A> & z.infer<B>

/**
 * Utility type for our API definitions.
 *
 * Note that all schemas (especially request body schemas)
 * should use zod codecs and not coercion or transforms,
 * to ensure that callers can safely use .encode() methods.
 */
export type Definition<
  ResponseSchema extends z.ZodType,
  RequestSchema extends z.ZodType,
  TPathSchema extends z.ZodType = z.ZodVoid,
  ErrorType = ErrorLike,
  QuerySchema extends z.ZodType = z.ZodVoid,
  HeaderSchema extends z.ZodType = z.ZodVoid,
  GatewayPath extends Readonly<string> = string
> = {
  /**
   * HTTP method for this API.
   * Defaults to 'GET' if not specified.
   */
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'

  /**
   * Zod schema for validating and typing URL query parameters.
   */
  querySchema?: QuerySchema

  /**
   * Zod schema defining the path parameters. This, along with querySchema,
   * is the source of truth for typing `pathFor`, `queryKey`, and `invalidateQueries`,
   * ensuring they stay in sync. Also enables runtime validation and automatic tool
   * generation for AI agents.
   *
   * Defaults to an empty schema if not provided.
   */
  pathSchema?: TPathSchema

  /**
   * Constructs the URL path from the validated path parameters.
   */
  pathFor: (params: CombinedSchemaOutput<TPathSchema, QuerySchema>) => string

  /**
   * How the request body should be serialized. Defaults to JSON.
   */
  requestBodyEncoding?: 'json' | 'form-data' | 'url-encoded'

  /**
   * Zod schema for validating and typing the response body.
   */
  responseSchema: ResponseSchema

  /**
   * Transforms raw errors into a domain-specific error type. This is particularly
   * useful for communicating to clients how to interpret expected errors, without
   * forcing them to read and parse the body or status code.
   *
   * Should generally handle ApiError instances, but may also want to consider ZodError
   * for parsing failures, and should return a sensible default for unknown errors.
   */
  errorMap?: (error: ErrorLike) => Promise<ErrorType> | ErrorType

  /**
   * Generates a stable cache key for use with React Query or similar libraries.
   *
   * TODO: see if we can one day just specify dependencies between APIs instead of
   * explicit cache keys, and let usePanoramaQuery figure out the details.
   */
  queryKey?: (params: NoInfer<CombinedSchemaOutput<TPathSchema, QuerySchema>>) => unknown[]

  /**
   * Returns query keys to invalidate when this API mutates data. Return a single
   * key (e.g. `[orgCode, 'boundaries']`) or, when a mutation affects more than one
   * cached resource, an array of keys (e.g. `[[orgCode, 'boundaries'], [orgCode,
   * 'field-plans']]`). Keys are matched by prefix, so a key never begins with an
   * array — that is how the two forms are told apart.
   */
  invalidateQueries?: (
    params: NoInfer<CombinedSchemaOutput<TPathSchema, QuerySchema>>
  ) => unknown[] | unknown[][]

  /**
   * Cache duration in seconds. Used for `gcTime` in React Query hooks and
   * for `Cache-Control` headers on responses.
   */
  ttlSeconds?: number

  /**
   * Zod schema for validating and typing the request body.
   */
  requestBodySchema?: RequestSchema

  /**
   * Zod schema for validating and typing request headers.
   */
  headersSchema?: HeaderSchema

  /**
   * The path pattern as defined in API Gateway, using `:param` style
   * placeholders. Used for routing configuration and documentation.
   */
  gatewayPath?: GatewayPath

  /**
   * Whether the API supports Zod codecs for encoding/decoding the request body.
   * Will be removed once all APIs have been migrated.
   *
   * NOTE: If you are migrating any APIs, this field can be set to true if the
   * requestBodySchema does not contain .transform() or .coerce() calls (or
   * other codec-incompatible patterns).
   */
  supportsCodec?: boolean

  /**
   * Tool name for AI agent integration. Must contain only letters, numbers,
   * underscores, or hyphens (max 64 characters).
   */
  name: string

  /**
   * Human-readable description of what this API does, used to help AI agents
   * decide when and how to call it.
   */
  description: string

  /**
   * Permissions required to access this API.
   *
   * Only use a PlatformWidePermission if the API is not specific to any operation.
   * Use an ExternalAuthorization value for endpoints that are not gated by a
   * Panorama permission at all (e.g. partner/external-identity authenticated).
   */
  requiredPermissions: OperationLevelPermission | PlatformWidePermission | ExternalAuthorization
}

/**
 * Defines an HTTP API contract with full type inference.
 *
 * Validates that `pathFor`, `queryKey`, and `invalidateQueries` are typed
 * consistently with `pathSchema`, catching parameter name mismatches at
 * compile time. Returns the definition unchanged, preserving any additional
 * properties for downstream consumers.
 *
 * Why do we enumerate all the type parameters? You might expect this to work:
 *
 * ```ts
 * function defineApi<T extends Definition<any, any, ...>>(definition: T): T
 * ```
 *
 * It doesn't. When the constraint uses `any`, TypeScript infers `pathFor`'s
 * parameter as `any` too — the connection to `pathSchema` is lost. By
 * declaring each type parameter explicitly and letting TypeScript infer them
 * from the value you pass, we preserve the constraint that `pathFor` must
 * match `pathSchema`.
 */
export function defineApi<
  TResponse extends z.ZodType,
  TRequest extends z.ZodType = z.ZodVoid,
  TPathSchema extends z.ZodType = z.ZodVoid,
  TError = ErrorLike,
  TQuery extends z.ZodType = z.ZodVoid,
  THeader extends z.ZodType = z.ZodVoid,
  TGateway extends Readonly<string> = string,
  TExtra extends Record<string, unknown> = {}
>(
  definition: TExtra &
    Definition<TResponse, TRequest, TPathSchema, TError, TQuery, THeader, TGateway>
) {
  return definition
}

export namespace PanoramaHttpApi {
  export type PathParams<T> =
    T extends Definition<
      infer _R,
      infer _B,
      infer TPath,
      infer _E,
      infer TQuery,
      infer _H,
      infer _G
    >
      ? CombinedSchemaOutput<TPath, TQuery>
      : never

  export type ParsedResponse<T> =
    T extends Definition<infer R, infer _B, infer _P, infer _E, infer _Q, infer _H, infer _G>
      ? z.infer<R>
      : never

  export type RawRequestBody<T> =
    T extends Definition<infer _R, infer B, infer _P, infer _E, infer _Q, infer _H, infer _G>
      ? B extends z.ZodVoid
        ? undefined
        : z.infer<B>
      : never

  export type MappedError<T> =
    T extends Definition<infer _R, infer _B, infer _P, infer E, infer _Q, infer _H, infer _G>
      ? E
      : never

  export type ApiDefinition<
    TResponse extends z.ZodType,
    TRequest extends z.ZodType = z.ZodVoid,
    TPathSchema extends z.ZodType = z.ZodVoid,
    TError = ErrorLike,
    TQuery extends z.ZodType = z.ZodVoid,
    THeader extends z.ZodType = z.ZodVoid,
    TGateway extends Readonly<string> = string
  > = Definition<TResponse, TRequest, TPathSchema, TError, TQuery, THeader, TGateway>

  /**
   * Matches any {@link ApiDefinition} regardless of its schema type arguments.
   *
   * Use this ONLY as a generic *constraint* (`<T extends AnyApiDefinition>`),
   * never as a plain value type. Callers still bind the concrete definition to
   * the generic `T`, so everything derived from it — `PathParams<T>`,
   * `RawRequestBody<T>`, `ParsedResponse<T>` — stays fully typed. The `any`s
   * live purely in the constraint bound; they do not widen anything at the
   * call site.
   *
   * `any` is required here (not `unknown` or `z.ZodType`): the schema params
   * appear in `pathFor`'s parameter position, which is contravariant, so a
   * concrete `ApiDefinition<SpecificSchema>` is not assignable to
   * `ApiDefinition<z.ZodType>`. Only the bivariant `any` bound matches every
   * definition.
   */
  /* The alias bodies below are the sole sanctioned `any` escape hatches for
   * matching-constraint positions. They must be `any` (not `unknown`/`z.ZodType`):
   * the schema params appear in `pathFor`'s parameter position, which is
   * contravariant, so a concrete `ApiDefinition<SpecificSchema>` is not
   * assignable to `ApiDefinition<z.ZodType>` — only a bivariant bound matches
   * every definition. Use each ONLY as a generic constraint; callers still bind
   * the concrete definition to the type parameter, so PathParams/RawRequestBody/
   * ParsedResponse stay fully typed. A block disable is used because oxfmt wraps
   * the longer aliases across lines, which would defeat a next-line disable. */
  /* oxlint-disable typescript/no-explicit-any -- see the note above: constraint-only escape hatches */

  /** Matches any {@link ApiDefinition} regardless of its schema type arguments. */
  export type AnyApiDefinition = ApiDefinition<any, any, any, any, any, any, any>

  /** Like {@link AnyApiDefinition}, but pins the request body to `z.ZodVoid`. */
  export type VoidBodyApiDefinition = ApiDefinition<any, z.ZodVoid, any, any, any, any, any>

  /** Like {@link AnyApiDefinition}, but pins the response schema to `TResponse`. */
  export type ResponseApiDefinition<TResponse extends z.ZodType> = ApiDefinition<
    TResponse,
    any,
    any,
    any,
    any,
    any,
    any
  >

  /* oxlint-enable typescript/no-explicit-any */
}
